import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { ReturnsCandidatesPage } from "@/features/reports/returns-candidates-page";
import { ensureElementVisible } from "@/lib/route-guards";

// URL search schema — wszystkie filtry deep-linkowalne. Wpisuje sie w pattern z
// `kartoteka/towary` (Faza 3 retrofitu DataTable na serverFiltering=true).
//
// Top-bar shortcut'y: scope/dateFrom/dateTo/bloz07/idfirm/rodzaj
// Column filters (z DataTable column header popup): *Like + statusPlatnosci
// Sortowanie: sortBy/sortDir
const searchSchema = z.object({
	scope: z.string().optional(),
	dateFrom: z.string().optional(),
	dateTo: z.string().optional(),
	bloz07: z.string().optional(),
	// TanStack Router default parser auto-koerc'uje numeric search-paramy do number,
	// wiec akceptujemy oba i normalizujemy do number (idfirm to KSID — i64 na backendzie).
	idfirm: z
		.union([z.string(), z.number()])
		.transform((v) => (typeof v === "number" ? v : Number(v)))
		.refine((v) => Number.isFinite(v) && v > 0, { error: "idfirm musi byc dodatnia" })
		.optional(),
	rodzaj: z.string().optional(),
	sortBy: z.string().optional(),
	sortDir: z.enum(["asc", "desc"]).optional(),
	// Column-level filters
	aptekaLike: z.string().optional(),
	dostawcaLike: z.string().optional(),
	regionLike: z.string().optional(),
	producentLike: z.string().optional(),
	kierownikLike: z.string().optional(),
	nazwaTowrLike: z.string().optional(),
	nazwaBlozLike: z.string().optional(),
	symzrLike: z.string().optional(),
	seriaLike: z.string().optional(),
	statusPlatnosci: z.string().optional(),
	// Date range column filters (Faza 4 — 2026-05-12)
	datwzFrom: z.string().optional(),
	datwzTo: z.string().optional(),
	datasFrom: z.string().optional(),
	datasTo: z.string().optional(),
	datapFrom: z.string().optional(),
	datapTo: z.string().optional(),
	// Numeric range column filters (Faza 4 — 2026-05-12). zod 4 odrzuca Infinity
	// i NaN w z.number() sam, wiec dawne `.finite()` (dzis no-op) odpadlo.
	dniDoWygasuMin: z.coerce.number().optional(),
	dniDoWygasuMax: z.coerce.number().optional(),
	nrpozMin: z.coerce.number().optional(),
	nrpozMax: z.coerce.number().optional(),
	ilzklMin: z.coerce.number().optional(),
	ilzklMax: z.coerce.number().optional(),
	ilaktMin: z.coerce.number().optional(),
	ilaktMax: z.coerce.number().optional(),
	czfntMin: z.coerce.number().optional(),
	czfntMax: z.coerce.number().optional(),
	czlntMin: z.coerce.number().optional(),
	czlntMax: z.coerce.number().optional(),
	vatzkMin: z.coerce.number().optional(),
	vatzkMax: z.coerce.number().optional(),
	wartoscZwrotuNettoMin: z.coerce.number().optional(),
	wartoscZwrotuNettoMax: z.coerce.number().optional(),
	wartoscLekospisNettoMin: z.coerce.number().optional(),
	wartoscLekospisNettoMax: z.coerce.number().optional(),
	// Derived columns (Faza 5 — 2026-05-13)
	stanOgolemMin: z.coerce.number().optional(),
	stanOgolemMax: z.coerce.number().optional(),
	rotacja60dMin: z.coerce.number().optional(),
	rotacja60dMax: z.coerce.number().optional(),
});

export type ZwrotyKwartalneSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_auth/raporty/zwroty-kwartalne")({
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(
			context.queryClient,
			"sidebar.raporty.zwroty-kwartalne",
			location.pathname,
		);
	},
	component: ReturnsCandidatesPage,
	validateSearch: (search: Record<string, unknown>): ZwrotyKwartalneSearch =>
		searchSchema.parse(search),
});
